import Pagination from '../pagination';
import {
  Box,
  Table as ChakraTable,
  TableProps as ChakraTableProps,
  TableBodyProps,
  TableContainer,
  TableContainerProps,
  TableHeadProps,
} from '@chakra-ui/react';
import React from 'react';
import { Columns } from './duck';
import { TBody, THead } from './components';

interface TableProps extends Omit<ChakraTableProps, 'children'> {
  data: any;
  theadProps?: TableHeadProps;
  theadHidden?: boolean;
  tbodyProps?: TableBodyProps;
  loading?: boolean;
  rowSelection?: {
    selectedKeys: (string | number)[];
    onChange(keys: (string | number)[]): void;
  };
  children: Columns;
  totalCount?: number;
  perPage?: number;
  page?: number;
  onPageChange?(page: number): void;
  tableContainerProps?: TableContainerProps;
}

const Table: React.FC<TableProps> = ({
  data = [],
  children,
  theadProps,
  theadHidden,
  tbodyProps,
  loading,
  rowSelection,
  totalCount,
  perPage,
  page,
  onPageChange,
  tableContainerProps,
  ...rest
}) => {
  const columns = (React.Children.toArray(children) as Columns).map(
    (child) => child.props
  );

  const handleSelect = (key: number | string) => {
    if (rowSelection) {
      const selected = rowSelection.selectedKeys.includes(key)
        ? rowSelection.selectedKeys.filter((val) => val !== key)
        : [...rowSelection.selectedKeys, key];

      rowSelection.onChange(selected);
    }
  };

  const handleSelectAll = (selectAll: boolean) => {
    if (rowSelection && selectAll) {
      rowSelection.onChange(data.map((record: any) => record._id || record.id));
    } else if (rowSelection && !selectAll) {
      rowSelection.onChange([]);
    }
  };

  const hasVisiblePagination =
    typeof totalCount === 'number' && totalCount > (perPage || 20);

  return (
    <Box w="100%">
      <TableContainer
        borderTopLeftRadius="lg"
        borderTopRightRadius="lg"
        borderBottomLeftRadius={hasVisiblePagination ? 'none' : 'lg'}
        borderBottomRightRadius={hasVisiblePagination ? 'none' : 'lg'}
        overflowX="auto"
        css={{
          '&::-webkit-scrollbar': {
            height: '4px',
            backgroundColor: '#313143',
          },
          '&::-webkit-scrollbar-track': {
            height: '4px',
            backgroundColor: '#EDF2F7',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#313143',
            borderRadius: '24px',
          },
        }}
        bg="white"
        display="flex"
        {...tableContainerProps}
      >
        <ChakraTable fontSize="sm" {...rest}>
          <THead
            {...theadProps}
            theadHidden={theadHidden}
            columns={columns}
            onSelectAll={
              rowSelection && !!data.length ? handleSelectAll : undefined
            }
            isIndeterminate={(rowSelection?.selectedKeys?.length || 0) > 0}
            isAllSelected={
              rowSelection?.selectedKeys?.length === data.length &&
              data.length > 0
            }
          />
          <TBody
            {...tbodyProps}
            data={data}
            loading={loading}
            columns={columns}
            onRowSelect={rowSelection && handleSelect}
            selectedRows={rowSelection?.selectedKeys || []}
          />
        </ChakraTable>
      </TableContainer>
      {totalCount !== undefined && (
        <Pagination
          page={page}
          onChange={onPageChange}
          hideOnSinglePage
          perPage={perPage}
          total={totalCount}
          bg="white"
          borderBottomRightRadius="lg"
          borderBottomLeftRadius="lg"
        />
      )}
    </Box>
  );
};

export default Table;
